import { type ReactNode, useState } from 'react';
import { GameRulesSection } from './game-rules-section';
import { RulesContent1 } from './rules-content-1';
import { RulesContent2 } from './rules-content-2';

export type Part = {
  id: string;
  Content: () => ReactNode;
};

export const PART_1: Part = { id: 'rules-1', Content: RulesContent1 };
export const PART_2: Part = { id: 'rules-2', Content: RulesContent2 };

// Server-side mirroring (rules-2 → rules_acknowledged_at) is automatic via
// /api/videos/progress. We just refresh the auth user so the route guards
// (`useIsGameRulesGated`) pick up the new value.
export async function ackRules(refresh: () => Promise<void>): Promise<void> {
  try {
    await refresh();
  } catch {
    /* idempotent — ignore */
  }
}

function scrollToSection2(): void {
  document
    .getElementById('rules-2')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export type RulesState = {
  w1: boolean;
  w2: boolean;
  u2: boolean;
  markW1: () => void;
  markW2: () => void;
  advance: () => void;
};

export function useRulesState(acked: boolean): RulesState {
  const [s, set] = useState({ w1: acked, w2: acked, u2: acked });
  return {
    w1: s.w1,
    w2: s.w2,
    u2: s.u2,
    markW1: () => set((p) => ({ ...p, w1: true })),
    markW2: () => set((p) => ({ ...p, w2: true })),
    advance: () => {
      set((p) => ({ ...p, u2: true }));
      setTimeout(scrollToSection2, 50);
    },
  };
}

type PartProps = {
  part: Part;
  watched: boolean;
  onWatched: () => void;
  onClick: () => void;
};

export function PartSection({ part, ...rest }: PartProps): ReactNode {
  const Content = part.Content;
  return (
    <GameRulesSection
      id={part.id}
      videoKey={part.id}
      watched={rest.watched}
      onVideoCompleted={rest.onWatched}
      onClick={rest.onClick}
    >
      <Content />
    </GameRulesSection>
  );
}
